// Iter77 — Market Scanner Bridge + Strategy Scanner Source Selector.
//
// Read-only Dashboard tile that lets the operator select tickers from
// one of five sources (Manual / Market Scanner cache / Watchlist /
// S&P 500 / Russell 2000) and a single strategy module (BPCS / BCCS /
// Iron Condor / CFM-CC-SHARES / CFM-CC-LEAPS), then generates a
// scanner-ready payload DRAFT — never auto-submitted, never executed.
//
// HARD CONSTRAINTS (asserted by Iter77 tests):
//  * Two — and only two — outbound HTTP call sites, both `axios.get`
//    targeting EXISTING read-only endpoints:
//      - GET /api/market-scanner/results
//      - GET /api/watchlist
//    Each fires exactly once per manual "Load source" click, never
//    on mount, never on a recurring timer, never automatically.
//  * NEVER calls /market-scanner/start, /market-scanner/cancel,
//    /market-scanner/status. NEVER mutates the scan cache.
//  * NEVER calls a strategy scanner / preview / candidates /
//    journal / broker / account endpoint.
//  * No `useEffect` that fires HTTP. The only `useEffect` (if any)
//    is forbidden by spec — we use explicit-click loading instead.
//  * No ranking. No "winner" selection. No recommendation.
//  * Payload draft is generated entirely client-side from already-
//    fetched row data + static universes. The tile never fabricates
//    option-chain bid/ask, never fabricates broker positions, never
//    fabricates account context — placeholders are surfaced with
//    explicit warnings.

import React, { useRef, useState } from "react";
import axios from "axios";
import {
  AlertTriangle, ChevronDown, ChevronUp, Play, Eraser, Copy,
  ShieldAlert, FileJson, Layers, Network, Bookmark, Globe,
  RefreshCw, ListFilter,
} from "lucide-react";
import { API } from "@/App";
import {
  SP500_UNIVERSE, RUSSELL_1000_ADDITIONS, RUSSELL_2000_UNIVERSE,
} from "@/components/marketUniverses";

// Single allowed endpoints — hardcoded text constants, never derived
// from user input. Used ONLY as the URL target for the manual Load
// source clicks below.
const MARKET_SCANNER_RESULTS_ENDPOINT = "/market-scanner/results";
const WATCHLIST_ENDPOINT              = "/watchlist";

// ── Source registry ──────────────────────────────────────────────────

const SOURCES = [
  { id: "MANUAL_SYMBOLS",
    label: "Manual Symbols",
    icon: ListFilter,
    available: true,
    description: "Type or paste comma- / newline-separated tickers." },
  { id: "MARKET_SCANNER_RESULTS",
    label: "Latest Market Scanner Results",
    icon: Network,
    available: true,
    description:
      "Reads the latest COMPLETED Market Scanner row cache via " +
      "GET /api/market-scanner/results. Never triggers a new scan." },
  { id: "WATCHLIST",
    label: "Watchlist",
    icon: Bookmark,
    available: true,
    description:
      "Reads the operator's CURRENTLY-ACTIVE named watchlist via " +
      "GET /api/watchlist?scope=active. Switch lists from Market " +
      "Forge / Dashboard / Strategy Scanner." },
  { id: "SP500_SCREENED",
    label: "S&P 500 Screened",
    icon: Globe,
    available: true,
    description:
      "Static frozen mirror of the engine's S&P 500 universe. " +
      "No backend call." },
  { id: "RUSSELL_2000_SCREENED",
    label: "Russell 2000 Screened",
    icon: Globe,
    available: RUSSELL_2000_UNIVERSE !== null,
    description:
      "Source unavailable: no safe read-only cache endpoint found. " +
      "This engine's universe is Russell 1000 (IWB), not Russell 2000." },
];

const MODULES = [
  { id: "BPCS",          label: "BPCS",           accent: "text-[#22D3FF]" },
  { id: "BCCS",          label: "BCCS",           accent: "text-[#FF3366]" },
  { id: "IRON_CONDOR",   label: "Iron Condor",    accent: "text-[#FFB800]" },
  { id: "CFM_CC_SHARES", label: "CFM-CC-SHARES",  accent: "text-[#22D3CC]" },
  { id: "CFM_CC_LEAPS",  label: "CFM-CC-LEAPS",   accent: "text-[#A855F7]" },
];

const REGIME_CHOICES = [
  "ANY", "SUPPORT_RECLAIM", "RESISTANCE_REJECT",
  "VALUE_COMPRESSION", "TREND_UP", "TREND_DOWN", "RANGE_BOUND",
];

// ── Symbol parsing helpers ───────────────────────────────────────────

function parseManualSymbols(raw) {
  if (!raw) return [];
  return Array.from(new Set(
    raw.split(/[,\n\r\s]+/)
       .map(s => s.trim().toUpperCase())
       .filter(s => /^[A-Z][A-Z0-9.\-]*$/.test(s)),
  ));
}

// ── Filter logic — applied ONLY to Market-Scanner-Results rows ──────

function applyRowFilters(rows, f) {
  if (!Array.isArray(rows)) return [];
  return rows.filter(r => {
    if (!r || typeof r !== "object") return false;
    const wh = r.wes_hourly || {};
    const csp = r.csp_75d || {};
    if (f.minWesP   != null && (wh.wes_p == null || wh.wes_p < f.minWesP))   return false;
    if (f.minWesC   != null && (wh.wes_c == null || wh.wes_c < f.minWesC))   return false;
    if (f.minCspRoi != null && (csp.roi  == null || csp.roi  < f.minCspRoi)) return false;
    if (f.minIvr    != null && (r.ivr    == null || r.ivr    < f.minIvr))    return false;
    if (f.regime    && f.regime !== "ANY") {
      const rg = wh.regime || r.regime;
      if (rg !== f.regime) return false;
    }
    if (f.validParityOnly && r.wes_parity_valid !== true) return false;
    return true;
  });
}

// ── Payload draft builder — strictly client-side ─────────────────────

function placeholderWes(row) {
  const wh = (row && row.wes_hourly) || {};
  return {
    val: wh.val ?? null, vah: wh.vah ?? null, poc: wh.poc ?? null,
    wes_p: wh.wes_p ?? null, wes_c: wh.wes_c ?? null,
    delta_VAL: wh.delta_VAL ?? null, delta_VAH: wh.delta_VAH ?? null,
    closed_bar_confirmed: wh.closed_bar_confirmed ?? false,
  };
}

function wesIsComplete(w) {
  return (
    Number.isFinite(w.val) && Number.isFinite(w.vah) &&
    Number.isFinite(w.poc) && Number.isFinite(w.wes_p) &&
    Number.isFinite(w.wes_c)
  );
}

function buildPerSymbolPayload(symbol, row, moduleId) {
  const r = row || {};
  const wes = placeholderWes(r);
  const payload = {
    symbol,
    spot_price:           r.spot ?? null,
    ivr:                  r.ivr ?? null,
    wes,
    chain:                [],   // Option chain placeholder.
    market_scanner_meta:  {
      company:           r.company ?? null,
      regime:            r.wes_hourly?.regime ?? r.regime ?? null,
      csp_75d_roi:       r.csp_75d?.roi ?? null,
      winner_pct:        r.winner ?? null,
      expiration:        r.expiration ?? null,
      wes_engine:        r.wes_engine ?? null,
      wes_parity_valid:  r.wes_parity_valid ?? null,
    },
  };
  if (moduleId === "CFM_CC_SHARES") {
    payload.account_context = {
      shares_owned: null, share_basis: null,
      total_call_credits: null, minimum_exit_price: null,
      assignment_policy: null, allow_buy_write: null,
    };
  } else if (moduleId === "CFM_CC_LEAPS") {
    delete payload.chain;
    payload.option_chains = { leaps_calls: [], short_calls: [] };
    payload.account_context = {
      total_short_call_credits: null,
      assignment_policy: null,
      allow_leaps: null,
      existing_leaps_position: null,
    };
  }
  return payload;
}

function buildPayloadDraft({ symbols, rowsBySymbol, moduleId }) {
  const watchlist = symbols.filter(Boolean);
  const payloads_by_symbol = {};
  const warnings = [];
  let missingWes = 0;
  for (const sym of watchlist) {
    const row = rowsBySymbol[sym] || null;
    const per = buildPerSymbolPayload(sym, row, moduleId);
    payloads_by_symbol[sym] = per;
    if (!wesIsComplete(per.wes)) missingWes += 1;
  }
  if (missingWes > 0) {
    warnings.push(
      `Full WES structure required before scanner run. ` +
      `${missingWes}/${watchlist.length} symbol(s) lack one or ` +
      `more of val / vah / poc / wes_p / wes_c.`,
    );
  }
  warnings.push(
    "Option chain required before scanner run. " +
    "The bridge never fabricates bid/ask/delta/strike rows.",
  );
  if (moduleId === "CFM_CC_SHARES") {
    warnings.push(
      "Share inventory context required. " +
      "Fill account_context.shares_owned / share_basis / " +
      "total_call_credits / minimum_exit_price / assignment_policy.",
    );
  }
  if (moduleId === "CFM_CC_LEAPS") {
    warnings.push(
      "LEAPS / short-call campaign context required. " +
      "Fill option_chains.leaps_calls + option_chains.short_calls + " +
      "account_context.allow_leaps / assignment_policy.",
    );
  }
  return {
    watchlist,
    payloads_by_symbol,
    max_symbols: watchlist.length,
    config: null,
    _draft_warnings: warnings,
  };
}

// ── Tile ─────────────────────────────────────────────────────────────

export default function MarketScannerBridgeTile() {
  const [open, setOpen]                   = useState(false);
  const [sourceId, setSourceId]           = useState("MANUAL_SYMBOLS");
  const [moduleId, setModuleId]           = useState("BPCS");
  const [manualText, setManualText]       = useState("");
  const [loading, setLoading]             = useState(false);
  const [loadError, setLoadError]         = useState(null);
  const [rows, setRows]                   = useState([]);
  const [rowsBySymbol, setRowsBySymbol]   = useState({});
  const [filters, setFilters]             = useState({
    minWesP: "", minWesC: "", minCspRoi: "", minIvr: "",
    regime: "ANY", validParityOnly: false,
  });
  const [draft, setDraft]                 = useState(null);
  const [copyState, setCopyState]         = useState("idle");
  const advancedRef = useRef(null);

  const source = SOURCES.find(s => s.id === sourceId);

  // ── Source loading helpers ──────────────────────────────────────
  async function loadMarketScannerCache() {
    setLoading(true); setLoadError(null);
    try {
      const r = await axios.get(`${API}${MARKET_SCANNER_RESULTS_ENDPOINT}`);
      const list = Array.isArray(r.data?.results) ? r.data.results : [];
      const byS  = {};
      for (const row of list) {
        if (row && row.symbol) byS[String(row.symbol).toUpperCase()] = row;
      }
      setRows(list);
      setRowsBySymbol(byS);
    } catch (e) {
      setLoadError(e?.response?.data?.detail ||
                    e?.message ||
                    "Failed to fetch market scanner results.");
      setRows([]);
      setRowsBySymbol({});
    } finally {
      setLoading(false);
    }
  }

  async function loadWatchlist() {
    setLoading(true); setLoadError(null);
    try {
      // Iter121.3 — Respect the user's currently-active watchlist so
      // the Bridge tile always reflects the same list the operator sees
      // on Dashboard / MarketForge / Strategy Scanner. The `scope=active`
      // param resolves server-side; keeps this component at exactly two
      // axios.get call sites (Iter77 doctrine preserved).
      const r = await axios.get(`${API}${WATCHLIST_ENDPOINT}?scope=active`);
      const list = Array.isArray(r.data) ? r.data : [];
      const byS = {};
      for (const row of list) {
        if (row && row.symbol) {
          byS[String(row.symbol).toUpperCase()] = { symbol: row.symbol };
        }
      }
      setRows(list);
      setRowsBySymbol(byS);
    } catch (e) {
      setLoadError(e?.response?.data?.detail ||
                    e?.message ||
                    "Failed to fetch watchlist.");
      setRows([]); setRowsBySymbol({});
    } finally {
      setLoading(false);
    }
  }

  function loadStaticUniverse(list, name) {
    setLoadError(null);
    if (!list) {
      setLoadError(`${name} source unavailable: no safe read-only data available.`);
      setRows([]); setRowsBySymbol({}); return;
    }
    const symbols = list.map(s => String(s).toUpperCase());
    setRows(symbols.map(s => ({ symbol: s })));
    setRowsBySymbol(Object.fromEntries(symbols.map(s => [s, { symbol: s }])));
  }

  function handleLoadSource() {
    setDraft(null); setCopyState("idle");
    if (sourceId === "MANUAL_SYMBOLS") {
      const list = parseManualSymbols(manualText);
      setRows(list.map(s => ({ symbol: s })));
      setRowsBySymbol(Object.fromEntries(list.map(s => [s, { symbol: s }])));
      setLoadError(null);
      return;
    }
    if (sourceId === "MARKET_SCANNER_RESULTS") return loadMarketScannerCache();
    if (sourceId === "WATCHLIST")             return loadWatchlist();
    if (sourceId === "SP500_SCREENED")        return loadStaticUniverse(SP500_UNIVERSE, "S&P 500");
    if (sourceId === "RUSSELL_2000_SCREENED") return loadStaticUniverse(RUSSELL_2000_UNIVERSE, "Russell 2000");
  }

  function handleClear() {
    setSourceId("MANUAL_SYMBOLS");
    setModuleId("BPCS");
    setManualText("");
    setRows([]); setRowsBySymbol({});
    setFilters({ minWesP: "", minWesC: "", minCspRoi: "", minIvr: "",
                  regime: "ANY", validParityOnly: false });
    setDraft(null);
    setLoadError(null);
    setCopyState("idle");
  }

  // ── Filter normalisation + symbol preview ──────────────────────
  const filterObj = {
    minWesP:   filters.minWesP   === "" ? null : Number(filters.minWesP),
    minWesC:   filters.minWesC   === "" ? null : Number(filters.minWesC),
    minCspRoi: filters.minCspRoi === "" ? null : Number(filters.minCspRoi),
    minIvr:    filters.minIvr    === "" ? null : Number(filters.minIvr),
    regime:    filters.regime,
    validParityOnly: !!filters.validParityOnly,
  };
  const filteredRows = sourceId === "MARKET_SCANNER_RESULTS"
    ? applyRowFilters(rows, filterObj)
    : rows;
  const selectedSymbols = Array.isArray(filteredRows)
    ? filteredRows.map(r => r?.symbol).filter(Boolean)
    : [];

  // ── Build draft ────────────────────────────────────────────────
  function handleBuildDraft() {
    setCopyState("idle");
    const d = buildPayloadDraft({
      symbols: selectedSymbols,
      rowsBySymbol,
      moduleId,
    });
    setDraft(d);
  }

  function handleCopyDraft() {
    if (!draft) return;
    const text = JSON.stringify(draft, null, 2);
    try {
      navigator.clipboard.writeText(text).then(
        () => setCopyState("copied"),
        () => setCopyState("failed"),
      );
    } catch (_e) { setCopyState("failed"); }
  }

  // ── Render ─────────────────────────────────────────────────────

  return (
    <div
      data-testid="market-scanner-bridge-tile"
      className="rounded-sm border border-[#26262B] bg-[#0A0A0E] p-3"
    >
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="market-scanner-bridge-toggle"
        className="w-full flex items-center justify-between text-left"
      >
        <span className="flex items-center gap-2 text-[#22D3FF]">
          <Layers size={14} />
          <span className="text-[12px] font-mono uppercase tracking-[0.18em]">
            Strategy Scanner Source Selector
          </span>
          <span className="text-[9px] text-[#5F5F66] uppercase tracking-[0.18em]">
            · Iter77 · Market Scanner Bridge · manual run · no ranking
          </span>
        </span>
        {open
          ? <ChevronUp size={14} className="text-[#8A8A93]" />
          : <ChevronDown size={14} className="text-[#8A8A93]" />}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {/* Safety banner */}
          <div
            data-testid="market-scanner-bridge-banner"
            className="flex flex-wrap items-center gap-2 text-[9px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]"
          >
            <ShieldAlert size={11} className="text-[#FFB800]" />
            <span data-testid="market-scanner-bridge-banner-read-only">
              Read-only source selector.
            </span>
            <span data-testid="market-scanner-bridge-banner-uses-cache">
              · Uses existing scanner/cache rows only.
            </span>
            <span data-testid="market-scanner-bridge-banner-no-auto-run">
              · No scanner auto-run.
            </span>
            <span data-testid="market-scanner-bridge-banner-no-ranking">
              · No strategy ranking.
            </span>
            <span data-testid="market-scanner-bridge-banner-no-execution">
              · No trade execution.
            </span>
          </div>

          {/* Source selector */}
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] mb-1">
              1 · Ticker source
            </label>
            <div
              data-testid="market-scanner-bridge-source-selector"
              className="flex flex-wrap gap-2"
            >
              {SOURCES.map(s => {
                const Icon = s.icon;
                const selected = sourceId === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => s.available && setSourceId(s.id)}
                    disabled={!s.available}
                    data-testid={`market-scanner-bridge-source-${s.id}`}
                    aria-label={`Ticker source: ${s.label}` +
                                  (s.available ? "" : " (unavailable)")}
                    className={`px-3 py-1 rounded-sm border text-[10px] font-mono uppercase tracking-[0.14em] inline-flex items-center gap-1 ${
                      !s.available
                        ? "border-[#3F3F47] text-[#5F5F66] cursor-not-allowed bg-[#08080A]"
                        : selected
                          ? "border-[#22D3FF]/40 bg-[#22D3FF]/10 text-[#22D3FF]"
                          : "border-[#26262B] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47]"
                    }`}
                  >
                    <Icon size={11} />
                    {s.label}
                  </button>
                );
              })}
            </div>
            {source && (
              <p
                data-testid={`market-scanner-bridge-source-description-${source.id}`}
                className={`mt-1 text-[10px] font-mono ${
                  source.available ? "text-[#5F5F66]" : "text-[#FFB800]"
                }`}
              >
                {source.description}
              </p>
            )}
          </div>

          {/* Manual symbols editor */}
          {sourceId === "MANUAL_SYMBOLS" && (
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] mb-1">
                Manual symbols (comma or newline separated)
              </label>
              <textarea
                data-testid="market-scanner-bridge-manual-symbols"
                value={manualText}
                onChange={e => setManualText(e.target.value)}
                spellCheck={false}
                placeholder="SPY, QQQ, AAPL"
                className="w-full h-20 text-[10px] font-mono bg-[#08080A] border border-[#26262B] rounded-sm p-2 text-[#E0E0E0] focus:outline-none focus:border-[#22D3FF]/40"
              />
            </div>
          )}

          {/* Optional filters — only meaningful for Market Scanner cache */}
          {sourceId === "MARKET_SCANNER_RESULTS" && (
            <div
              data-testid="market-scanner-bridge-filters"
              className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[10px] font-mono"
            >
              <FilterInput
                label="min WES-P" value={filters.minWesP}
                onChange={v => setFilters({ ...filters, minWesP: v })}
                testid="market-scanner-bridge-filter-min-wes-p" />
              <FilterInput
                label="min WES-C" value={filters.minWesC}
                onChange={v => setFilters({ ...filters, minWesC: v })}
                testid="market-scanner-bridge-filter-min-wes-c" />
              <FilterInput
                label="min CSP 75d ROI" value={filters.minCspRoi}
                onChange={v => setFilters({ ...filters, minCspRoi: v })}
                testid="market-scanner-bridge-filter-min-csp-roi" />
              <FilterInput
                label="min IVR" value={filters.minIvr}
                onChange={v => setFilters({ ...filters, minIvr: v })}
                testid="market-scanner-bridge-filter-min-ivr" />
              <label className="flex flex-col">
                <span className="text-[#5F5F66] uppercase tracking-[0.12em] mb-1">
                  regime
                </span>
                <select
                  data-testid="market-scanner-bridge-filter-regime"
                  value={filters.regime}
                  onChange={e => setFilters({ ...filters, regime: e.target.value })}
                  className="bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[#E0E0E0]"
                >
                  {REGIME_CHOICES.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 mt-4">
                <input
                  type="checkbox"
                  data-testid="market-scanner-bridge-filter-valid-parity-only"
                  checked={filters.validParityOnly}
                  onChange={e => setFilters({ ...filters, validParityOnly: e.target.checked })}
                />
                <span className="text-[#8A8A93] uppercase tracking-[0.12em]">
                  valid parity only
                </span>
              </label>
            </div>
          )}

          {/* Module selector */}
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] mb-1">
              2 · Target strategy module
            </label>
            <div
              data-testid="market-scanner-bridge-module-selector"
              className="flex flex-wrap gap-2"
            >
              {MODULES.map(m => {
                const selected = moduleId === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setModuleId(m.id)}
                    data-testid={`market-scanner-bridge-module-${m.id}`}
                    aria-label={`Target module: ${m.label}`}
                    className={`px-3 py-1 rounded-sm border text-[10px] font-mono uppercase tracking-[0.14em] ${
                      selected
                        ? `border-current bg-current/10 ${m.accent}`
                        : "border-[#26262B] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47]"
                    }`}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Action bar */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleLoadSource}
              disabled={loading || !source?.available}
              data-testid="market-scanner-bridge-load"
              aria-label="Load tickers from the selected source"
              className="px-3 py-1 rounded-sm border border-[#22D3FF]/40 bg-[#22D3FF]/10 text-[10px] font-mono uppercase tracking-[0.14em] text-[#22D3FF] hover:bg-[#22D3FF]/20 inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
              {loading ? "Loading…" : "Load Source"}
            </button>
            <button
              type="button"
              onClick={handleBuildDraft}
              disabled={selectedSymbols.length === 0}
              data-testid="market-scanner-bridge-build-draft"
              aria-label="Build strategy payload draft"
              className="px-3 py-1 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/10 text-[10px] font-mono uppercase tracking-[0.14em] text-[#FFB800] hover:bg-[#FFB800]/20 inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Play size={11} />
              Build Strategy Payload Draft
            </button>
            {draft && (
              <button
                type="button"
                onClick={handleCopyDraft}
                data-testid={
                  copyState === "copied" ? "market-scanner-bridge-copy-success"
                    : copyState === "failed" ? "market-scanner-bridge-copy-failed"
                      : "market-scanner-bridge-copy"
                }
                aria-label="Copy payload draft to clipboard"
                className="px-3 py-1 rounded-sm border border-[#26262B] text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
              >
                <Copy size={11} />
                {copyState === "copied" ? "✓ Copied"
                  : copyState === "failed" ? "× Copy failed"
                    : "Copy Payload Draft"}
              </button>
            )}
            <button
              type="button"
              onClick={handleClear}
              data-testid="market-scanner-bridge-clear"
              aria-label="Clear bridge state"
              className="px-3 py-1 rounded-sm border border-[#26262B] text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
            >
              <Eraser size={11} />
              Clear
            </button>
          </div>

          {/* Error block */}
          {loadError && (
            <div
              data-testid="market-scanner-bridge-load-error"
              className="text-[10px] font-mono text-[#FF3366] flex items-start gap-1"
            >
              <AlertTriangle size={11} className="mt-0.5 shrink-0" />
              <span>{loadError}</span>
            </div>
          )}

          {/* Symbol preview */}
          <div
            data-testid="market-scanner-bridge-symbol-preview"
            className="rounded-sm border border-[#26262B] bg-[#08080A] p-2"
          >
            <div className="flex items-center justify-between text-[10px] font-mono mb-1">
              <span className="text-[#5F5F66] uppercase tracking-[0.14em]">
                Selected tickers
              </span>
              <span className="text-[#E0E0E0]">
                <span data-testid="market-scanner-bridge-symbol-count">
                  {selectedSymbols.length}
                </span>{" "}
                · source:{" "}
                <span
                  data-testid="market-scanner-bridge-source-label"
                  className="text-[#22D3FF] uppercase tracking-[0.12em]"
                >
                  {source?.label || "—"}
                </span>
              </span>
            </div>
            {selectedSymbols.length === 0 ? (
              <p className="text-[10px] font-mono text-[#5F5F66]">
                No symbols loaded. Choose a source and click Load Source.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {selectedSymbols.slice(0, 200).map(sym => (
                  <span
                    key={sym}
                    data-testid={`market-scanner-bridge-symbol-chip-${sym}`}
                    className="px-1.5 py-0.5 rounded-sm border border-[#26262B] bg-[#0A0A0E] text-[9px] font-mono text-[#E0E0E0]"
                  >
                    {sym}
                  </span>
                ))}
                {selectedSymbols.length > 200 && (
                  <span className="px-1.5 py-0.5 text-[9px] font-mono text-[#5F5F66]">
                    + {selectedSymbols.length - 200} more
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Draft warnings */}
          {draft && Array.isArray(draft._draft_warnings) && draft._draft_warnings.length > 0 && (
            <div
              data-testid="market-scanner-bridge-draft-warnings"
              className="text-[10px] font-mono text-[#FFB800] space-y-0.5"
            >
              {draft._draft_warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-1">
                  <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}

          {/* Advanced / Developer Payload View — collapsed by default */}
          {draft && (
            <details
              ref={advancedRef}
              data-testid="market-scanner-bridge-advanced-payload"
              className="border border-[#26262B] rounded-sm"
            >
              <summary
                data-testid="market-scanner-bridge-advanced-summary"
                className="cursor-pointer px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0] flex items-center gap-1"
              >
                <FileJson size={11} />
                Advanced / Developer payload view
              </summary>
              <pre
                data-testid="market-scanner-bridge-advanced-pre"
                className="m-0 px-2 py-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] max-h-72 overflow-auto"
              >
                {JSON.stringify(draft, null, 2)}
              </pre>
            </details>
          )}

          {/* Disclaimer */}
          <div
            data-testid="market-scanner-bridge-disclaimer"
            className="text-[9px] font-mono text-[#5F5F66] uppercase tracking-[0.12em]"
          >
            Source bridge only. No scanner auto-run. No strategy ranking.
            No trade execution. No journal write. No broker action.
            Paste the generated payload into the relevant scanner panel
            and click Run there when ready.
          </div>
        </div>
      )}
    </div>
  );
}

function FilterInput({ label, value, onChange, testid }) {
  return (
    <label className="flex flex-col">
      <span className="text-[#5F5F66] uppercase tracking-[0.12em] mb-1">
        {label}
      </span>
      <input
        type="number"
        inputMode="decimal"
        data-testid={testid}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[#E0E0E0] focus:outline-none focus:border-[#22D3FF]/40"
      />
    </label>
  );
}
